import "@/App.css";
import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CallProvider } from "@/context/CallContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import MemberGuard from "@/components/MemberGuard";
import ConsortMark from "@/components/ConsortMark";

const Landing = lazy(() => import("@/pages/Landing"));
const Login = lazy(() => import("@/pages/Login"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const SharedHighlight = lazy(() => import("@/pages/SharedHighlight"));
const Register = lazy(() => import("@/pages/Register"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const VerifyEmailRequired = lazy(() => import("@/pages/VerifyEmailRequired"));
const VerifyPhone = lazy(() => import("@/pages/VerifyPhone"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const Welcome = lazy(() => import("@/pages/Welcome"));
const Discovery = lazy(() => import("@/pages/Discovery"));
const ProfileDetail = lazy(() => import("@/pages/ProfileDetail"));
const Matches = lazy(() => import("@/pages/Matches"));
const Chat = lazy(() => import("@/pages/Chat"));
const Likes = lazy(() => import("@/pages/Likes"));
const Subscription = lazy(() => import("@/pages/Subscription"));
const Billing = lazy(() => import("@/pages/Billing"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const Verification = lazy(() => import("@/pages/Verification"));
const Settings = lazy(() => import("@/pages/Settings"));
const MyProfile = lazy(() => import("@/pages/MyProfile"));
const Highlights = lazy(() => import("@/pages/Highlights"));
const Safety = lazy(() => import("@/pages/Safety"));
const Redeem = lazy(() => import("@/pages/Redeem"));
const Gift = lazy(() => import("@/pages/Gift"));
const GiftSuccess = lazy(() => import("@/pages/GiftSuccess"));
const GiftOpen = lazy(() => import("@/pages/GiftOpen"));
const Admin = lazy(() => import("@/pages/Admin"));

function PageLoader() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-5">
      <div className="consort-splash-mark text-primary" data-testid="app-splash">
        <ConsortMark size={54} strokeWidth={1.3} />
      </div>
      <div className="font-display text-2xl gold-text tracking-tight">Consort</div>
      <div className="h-[2px] w-24 overflow-hidden rounded-full bg-white/10">
        <div className="consort-splash-bar h-full w-1/2 bg-primary rounded-full" />
      </div>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/s/:token" element={<SharedHighlight />} />
        <Route path="/gift/open/:code" element={<GiftOpen />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/verify-email-required" element={<ProtectedRoute><VerifyEmailRequired /></ProtectedRoute>} />
        <Route path="/verify-phone" element={<ProtectedRoute><VerifyPhone /></ProtectedRoute>} />
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
        <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
        <Route path="/redeem" element={<ProtectedRoute><Redeem /></ProtectedRoute>} />
        <Route path="/gift/success" element={<ProtectedRoute><GiftSuccess /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />

        <Route element={<MemberGuard />}>
          <Route path="/discovery" element={<Discovery />} />
          <Route path="/u/:id" element={<ProfileDetail />} />
          <Route path="/matches" element={<Matches />} />
          <Route path="/chat/:matchId" element={<Chat />} />
          <Route path="/likes" element={<Likes />} />
          <Route path="/subscription" element={<Subscription />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/verification" element={<Verification />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/profile" element={<MyProfile />} />
          <Route path="/highlights" element={<Highlights />} />
          <Route path="/gift" element={<Gift />} />
          <Route path="/safety" element={<Safety />} />
        </Route>

        <Route path="*" element={<Navigate to="/discovery" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CallProvider>
            <AppRoutes />
            <Toaster position="top-center" theme="dark" richColors />
          </CallProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
